import { useEffect, useState } from "react";
import { getMe, logout, type User } from "./api";
import { SignIn } from "./components/SignIn";
import { Dashboard } from "./components/Dashboard";
import { AdminWorkspace } from "./components/AdminWorkspace";
import { CuratorPanel } from "./components/CuratorPanel";
import { GettingStarted } from "./components/GettingStarted";

type Route = "home" | "admin" | "curator" | "getting-started";

function pathToRoute(pathname: string): Route {
  if (pathname.startsWith("/admin")) return "admin";
  if (pathname.startsWith("/curator")) return "curator";
  if (pathname.startsWith("/getting-started")) return "getting-started";
  return "home";
}

export function App() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [route, setRoute] = useState<Route>(pathToRoute(window.location.pathname));

  useEffect(() => {
    getMe().then(setUser).catch(() => setUser(null));
  }, []);

  useEffect(() => {
    const onPop = () => setRoute(pathToRoute(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  function navigate(path: string) {
    window.history.pushState({}, "", path);
    setRoute(pathToRoute(path));
  }

  async function handleSignOut() {
    await logout();
    setUser(null);
    navigate("/");
  }

  if (user === undefined) {
    return <div className="empty">Loading…</div>;
  }

  // Getting-started page is public — accessible whether signed in or not.
  // Render it BEFORE the sign-in gate so a curator can deep-link a new
  // admin straight to the walkthrough without forcing sign-in first.
  if (route === "getting-started" && !user) {
    return (
      <GettingStarted
        isSignedIn={false}
        onBack={() => navigate("/")}
      />
    );
  }

  if (!user) {
    return (
      <SignIn
        onSignedIn={() => getMe().then(setUser)}
        onShowGettingStarted={() => navigate("/getting-started")}
      />
    );
  }

  return (
    <>
      <header className="top">
        <div
          className="brand"
          onClick={() => navigate("/")}
          style={{ cursor: "pointer" }}
        >
          <img src="/apple-touch-icon.png" alt="" />
          <h1>
            Trail Status<span className="pro">Pro</span>
          </h1>
        </div>
        <nav>
          <a
            href="/"
            className={route === "home" ? "active" : ""}
            onClick={(e) => { e.preventDefault(); navigate("/"); }}
          >Home</a>
          <a
            href="/admin"
            className={route === "admin" ? "active" : ""}
            onClick={(e) => { e.preventDefault(); navigate("/admin"); }}
          >My Trails</a>
          {user.role === "curator" && (
            <a
              href="/curator"
              className={route === "curator" ? "active" : ""}
              onClick={(e) => { e.preventDefault(); navigate("/curator"); }}
            >Curator</a>
          )}
          <a
            href="/getting-started"
            className={route === "getting-started" ? "active" : ""}
            onClick={(e) => { e.preventDefault(); navigate("/getting-started"); }}
          >Help</a>
        </nav>
        <div className="actions">
          <span className="who">{user.email}</span>
          <button className="small secondary" onClick={handleSignOut}>
            Sign out
          </button>
        </div>
      </header>
      <main>
        {route === "home" && <Dashboard user={user} onNavigate={navigate} />}
        {route === "admin" && <AdminWorkspace />}
        {route === "curator" && user.role === "curator" && <CuratorPanel />}
        {route === "curator" && user.role !== "curator" && (
          <div className="empty">Curator access only.</div>
        )}
        {route === "getting-started" && (
          <GettingStarted isSignedIn={true} />
        )}
      </main>
      <footer>Trail Status Pro · admin console</footer>
    </>
  );
}
